// money.ts — plain-text price formatting shared between PriceDisplay's own
// spans and any guest-facing string that needs a price inline (e.g. a
// paraglide `{price}` placeholder). Kept out of price-display.tsx itself:
// that file must export ONLY the component — eslint's
// react-refresh/only-export-components flags a component file that also
// exports plain functions/constants, since mixing the two breaks Fast
// Refresh for that file.
//
// This is deliberately a DIFFERENT formatter from lib/booking.ts's
// formatMoney: that one is Intl-backed (`Intl.NumberFormat` with `style:
// 'currency'`) and always renders two decimals — right for a booking total
// (checkout/confirmation/cabinet), wrong for a catalog price sitting next to
// PriceDisplay's own no-decimals rendering. OH-63 Task 4 fixed exactly that
// mismatch: the apartment card's "from" price rendered via formatMoney
// ("€95.00") while its "exact" price rendered via PriceDisplay ("€110") —
// two money formats on one screen, since search results show both side by
// side. currencySymbol/groupAmount below are the single source of truth
// PriceDisplay and formatPriceMajor both read from, so the two can't drift
// apart again.

const SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', RSD: 'RSD' };

/** ISO 4217 currency symbol; unknown/multi-char codes (RSD, etc.) pass through as-is. */
export function currencySymbol(currency: string): string {
  return SYMBOLS[currency] ?? currency;
}

/**
 * Thousands-group an integer or decimal number string: 1234.5 → "1,234.50".
 *
 * A single fractional digit is padded to two (95.5 → "95.50",
 * 0.1 → "0.10") — money display needs the same number of decimal places
 * every time a fraction is shown, not however many `String(n)` happens to
 * print. This is minor-units arithmetic surfacing an odd number of cents
 * (a rate like 9550 minor units / 100 = 95.5 major), not a rounding
 * concern: `dec` never has more than 2 digits for a real money value, so
 * padding to exactly 2 is the whole fix. A whole number (no `.` at all,
 * e.g. 95 or 120) is untouched — "€95", not "€95.00".
 */
export function groupAmount(n: number): string {
  const parts = String(n).split('.');
  const int = parts[0] ?? '';
  const dec = parts[1];
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (dec === undefined) return grouped;
  const padded = dec.length === 1 ? `${dec}0` : dec;
  return `${grouped}.${padded}`;
}

// formatPriceMajor renders a display (major-unit) amount as the single
// plain-text string PriceDisplay's own symbol + amount spans read out, e.g.
// 95 EUR -> "€95", 12345 RSD -> "RSD 12,345". `amount` is major units, same
// contract as PriceDisplay's own `amount` prop (caller converts from the
// minor-unit int64 — CLAUDE.md money rule).
export function formatPriceMajor(amount: number, currency = 'EUR'): string {
  const sym = currencySymbol(currency);
  const isCode = sym.length > 1;
  return `${sym}${isCode ? ' ' : ''}${groupAmount(amount)}`;
}
